
import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { checkAdminStatus } from '@/services/admin/adminStatusService';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

export const useAdminStatus = () => {
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasChecked, setHasChecked] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Define verifyAdminStatus as useCallback to avoid recreation on each render
  const verifyAdminStatus = useCallback(async () => {
    // Prevent multiple simultaneous checks
    if (hasChecked) {
      return;
    }

    try {
      setIsLoading(true);
      setHasChecked(true);
      
      // Get current session for debugging
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        console.log('No session found in useAdminStatus');
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }
      
      // Check admin status using our enhanced service
      const { isAdmin, error } = await checkAdminStatus();
      
      console.log(`Admin check result: ${isAdmin} (user: ${sessionData.session.user.email})`);
      
      setIsAdmin(isAdmin);
      
      // Only show error toast for actual API errors, not verification failures
      if (error && error.message && !error.message.includes('No onboarding data found')) {
        console.error('Admin status check error:', error);
        toast({
          title: "Warning",
          description: "There was an issue verifying admin status. Some features may be limited.",
          variant: "destructive",
        });
      }

      // Redirect non-admin users away from admin pages
      if (!isAdmin && location.pathname.startsWith('/admin')) {
        console.log('Non-admin user attempting to access admin page, redirecting to dashboard');
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Admin verification error:', error);
      setIsAdmin(false);
      // Only show toast for unexpected errors
      toast({
        title: "System Error",
        description: "Unable to verify admin status. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [hasChecked, location.pathname, navigate, toast]);

  useEffect(() => {
    verifyAdminStatus();
  }, [verifyAdminStatus]);

  return {
    isAdmin,
    isLoading,
    verifyAdminStatus: () => {
      setHasChecked(false);
      verifyAdminStatus();
    }
  };
};
